import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/ui/Card';
import Pill from '../../components/ui/Pill';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import StatusBanner from '../../components/ui/StatusBanner';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { buildPersonWorkspacePath } from '../../config/workforceRouting';
import { useExpiringDocuments } from '../../hooks/useExpiringDocuments';

const TYPE_LABELS = {
  offer_letter: 'Offer letter',
  certification: 'Certification',
  other: 'Other'
};

function formatDate(iso) {
  if (!iso) return '—';
  return String(iso).slice(0, 10);
}

function StatusPill({ status, iso }) {
  const date = formatDate(iso);
  if (status === 'expired') return <Pill variant="danger" data-testid="expiring-row-expired">Expired {date}</Pill>;
  if (status === 'expiring_soon') return <Pill variant="warning" data-testid="expiring-row-soon">Expires {date}</Pill>;
  return <Pill variant="neutral" data-testid="expiring-row-ok">Expires {date}</Pill>;
}

function hasEmployeePermission(user, permission) {
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  return permissions.includes(permission) || permissions.includes('manage_users');
}

function ExpiringRow({ item, canDeepLink, onView }) {
  return (
    <tr className="employee-directory__row">
      <td>
        <div style={{ fontWeight: 600 }}>
          {item.displayName || item.managerName || (item.employeeProfileId ? `Employee #${item.employeeProfileId}` : `Roster record #${item.managerId}`)}
        </div>
        <div style={{ fontSize: 12, color: '#5b6471' }}>{TYPE_LABELS[item.documentType] || item.documentType}</div>
      </td>
      <td>{item.managerLocationCode || '—'}</td>
      <td>
        <StatusPill status={item.status} iso={item.expiresAt} />
      </td>
      <td style={{ textAlign: 'right' }}>
        {canDeepLink ? (
          <Button size="sm" variant="secondary" onClick={() => onView(item)}>Open workspace</Button>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * Tenant-wide operational panel of expiring/expired employee
 * documents. Designed to live at the top of the EmployeeDirectory
 * page — collapsible header, no fetch when the caller has no
 * documents.read permission.
 */
export default function ExpiringDocumentsPanel({ user = null, defaultExpanded = true }) {
  const navigate = useNavigate();
  const canRead = hasEmployeePermission(user, 'employees.documents.read');
  const canDeepLink = hasEmployeePermission(user, 'employees.read');

  const [withinDays, setWithinDays] = useState(30);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { items, status, error, refetch } = useExpiringDocuments({
    withinDays,
    includeExpired: true,
    enabled: canRead
  });

  const counts = useMemo(() => {
    const out = { expired: 0, expiring_soon: 0, ok: 0 };
    for (const it of items) {
      if (out[it.status] != null) out[it.status] += 1;
    }
    return out;
  }, [items]);

  if (!canRead) return null;

  const handleView = (item) => {
    if (!canDeepLink) return;
    navigate(buildPersonWorkspacePath({
      employeeProfileId: item.employeeProfileId,
      managerId: item.managerId
    }));
  };

  return (
    <Card padding="md" shadow="sm" data-testid="expiring-documents-panel">
      <div className="employee-documents__header">
        <h2 className="employee-documents__title">Expiring documents</h2>
        <div className="employee-documents__header-actions">
          <span style={{ fontSize: 12, color: '#5b6471' }}>
            {counts.expired} expired · {counts.expiring_soon} expiring soon
          </span>
          <label className="employee-documents__toggle">
            Within
            <select
              value={withinDays}
              onChange={(e) => setWithinDays(Number(e.target.value))}
              aria-label="Window in days"
              data-testid="expiring-within-days"
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
          </label>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setExpanded(v => !v)}
            data-testid="expiring-toggle"
          >
            {expanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>
      </div>

      {!expanded ? null : (
        <>
          {status === 'error' ? (
            <StatusBanner kind="error" message={error} onRetry={refetch} />
          ) : null}

          {status === 'loading' && items.length === 0 ? (
            <LoadingSkeleton variant="paragraph" lines={3} />
          ) : items.length === 0 ? (
            <EmptyState
              compact
              icon="·"
              headline="Nothing expiring"
              body={`No documents expire in the next ${withinDays} days.`}
            />
          ) : (
            <table className="employee-directory__table" data-testid="expiring-documents-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th aria-label="Row actions" />
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <ExpiringRow
                    key={item.id}
                    item={item}
                    canDeepLink={canDeepLink}
                    onView={handleView}
                  />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </Card>
  );
}
